import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  AudienceSet,
  AudienceSetTargets,
  AudienceSetTargetsData,
  AudienceSetTargetType,
  Brand,
  CampaignAdSet,
  ChangeEventType,
  DemographicsSet,
  FbTargetSearch,
  IGeolocationSet,
} from '../../../../../types';
import { FloatingButton, PrimaryButton } from '../../../../Buttons';
import { HtmlTooltip } from '../../../../Tooltip';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import Spinner from '../../../../Spinner';
import { numberShortener } from '../../../../../utils/numberFormatter';
import { constantStringToHumanized } from '../../../../../utils/stringModifier';
import AudienceList from './AudienceList';
import CustomAudienceSetForm from './CustomAudienceSetForm';
import { DiySettings } from '../../../../../types/IDiy';
import { FBOptimizationGoal } from '../../../../../utils/helpers/facebookAdsHelper';
import { getFbTargetingReachEstimate } from '../../../../../services/ads';
import {
  createOrUpdateAudienceSet,
  getBrandDemographicsSets,
  getBrandLocationSets,
  getSingleBrandAudienceSets,
} from '../../../../../services/assets';
import DetailedAudienceSetForm from './DetailedAudienceSetForm';
import {
  assetsSectionStyling,
  demographicsToText,
  geolocationToText,
  parsedDetailedTargeting,
} from '../../../../../utils/helpers/AssetHelpers';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import { isArray } from 'lodash';
import PageHeader from '../../../../PageHeader';
import PopupModal from '../../../../Modal';
import ModalHeader from '../../../../Modal/ModalHeader';

const DEFAULT_TARGET_VALUES: AudienceSetTargetType = {
  interests: [],
  behaviors: [],
  life_events: [],
  industries: [],
  income: [],
  family_statuses: [],
  education_schools: [],
  education_statuses: [],
  college_years: [],
  education_majors: [],
  work_employers: [],
  work_positions: [],
  relationship_statuses: [],
};

interface AudienceSetFormProps {
  brand: Brand;
  diySettings: DiySettings;
  onClose: () => void;
  edit?: boolean;
  audienceSetId?: string;
  isCreateAds?: boolean;
  // If used in Create Campaign Form
  audienceSets?: AudienceSet[];
  setAudienceSets?: Dispatch<SetStateAction<AudienceSet[]>>;
  setFilteredAudienceSets?: Dispatch<SetStateAction<AudienceSet[]>>;
  onSelectAudienceSet?: (audienceSet: AudienceSet) => void;
}

const AudienceSetForm: FC<AudienceSetFormProps> = ({
  brand,
  diySettings,
  onClose,
  edit = false,
  audienceSetId,
  isCreateAds = false,
  // If used in Create Campaign Form
  audienceSets,
  setAudienceSets,
  setFilteredAudienceSets,
  onSelectAudienceSet,
}) => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
  } = useForm({ mode: 'onSubmit' });
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<AudienceSet>({
    name: '',
    description: '',
    primary: true,
    minReach: 0,
    maxReach: 0,
    audienceType: 'custom',
    targets: {
      custom_audiences: [],
      excluded_custom_audiences: [],
      flexible_spec: [],
      exclusions: { ...DEFAULT_TARGET_VALUES },
      targeting_relaxation_types: {
        lookalike: 1,
        custom_audience: 0,
      },
      targeting_automation: {
        advantage_audience: 0,
      },
      targeting_optimization: 'none',
    },
  });
  const [reachEstimate, setReachEstimate] = useState({
    users_lower_bound: 0,
    users_upper_bound: 0,
  });
  const [reachError, setReachError] = useState<string>(null);
  const [reachLoading, setReachLoading] = useState<boolean>(false);
  const [defaultLocationSet, setDefaultLocationSet] =
    useState<IGeolocationSet>(null);
  const [defaultDemographicsSet, setDefaultDemographicsSet] =
    useState<DemographicsSet>(null);
  const [confirmEdit, setConfirmEdit] = useState<boolean>(false);

  useEffect(() => {
    if (edit && audienceSetId && brand) {
      getAudienceSet();
    }
  }, [edit, audienceSetId, brand]);

  useEffect(() => {
    if (brand) {
      getPrimaryLocationSet();
      getPrimaryDemographicsSet();
    }
  }, [brand]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const targets = parsedDetailedTargeting(formValues?.targets);

      let temp: any = {
        targeting: {
          ...targets,
          geo_locations: defaultLocationSet?.geolocations || {
            countries: [{ key: 'US', type: 'country' }],
          },
          excluded_geo_locations:
            defaultLocationSet?.excludedGeolocations || {},
        },
      };

      getFacebookTargetReach(temp.targeting);
    }, 2000);

    return () => clearTimeout(delayDebounceFn);
  }, [formValues?.targets, defaultLocationSet]);

  const getAudienceSet = async () => {
    try {
      const response = await getSingleBrandAudienceSets(
        brand?._id,
        audienceSetId,
      );

      setFormValues(response.data);
    } catch (error: any) {
      console.log(error);
    }
  };

  const getFacebookTargetReach = async (targeting: FbTargetSearch) => {
    try {
      setReachLoading(true);

      const response = await getFbTargetingReachEstimate({
        brandId: brand?._id,
        targets: targeting,
        optimizationGoal: FBOptimizationGoal.LeadGeneration,
      });

      setReachEstimate(response.data);
      setReachError(null);
    } catch (error: any) {
      setReachEstimate({
        users_lower_bound: 0,
        users_upper_bound: 0,
      });
      const err = error.response.data;
      setReachError(err?.message);
    } finally {
      setReachLoading(false);
    }
  };

  const getPrimaryLocationSet = async () => {
    try {
      const response = await getBrandLocationSets({
        brandId: brand._id,
        primary: true,
      });

      setDefaultLocationSet(response.data[0]);
    } catch (error: any) {
      console.log(error);
    }
  };

  const getPrimaryDemographicsSet = async () => {
    try {
      const response = await getBrandDemographicsSets({
        brandId: brand._id,
        primary: true,
      });

      setDefaultDemographicsSet(response.data[0]);
    } catch (error: any) {
      console.log(error);
    }
  };

  const isEmptyTargets = (targets: AudienceSetTargets) => {
    return Object.values(targets).every(
      (field) => Array.isArray(field) && field.length === 0,
    );
  };

  const handleOnSubmit = async () => {
    try {
      if (reachError) {
        setLoading(false);

        return dispatch(
          toggleAlert({
            toggle: true,
            message: reachError,
            type: 'error',
          }),
        );
      }

      if (formValues?.usageCount > 0 && !confirmEdit && edit) {
        return setConfirmEdit(true);
      }

      setLoading(true);

      let temp: AudienceSet = {
        ...formValues,
        minReach: reachEstimate?.users_lower_bound || 0,
        maxReach: reachEstimate?.users_upper_bound || 0,
      };

      const { usageCount, adsUsage, ...params } = temp;

      const response = await createOrUpdateAudienceSet({
        brandId: brand?._id,
        body: params,
        audienceSetId: edit ? audienceSetId : null,
      });

      const data: AudienceSet = response.data;

      if (isCreateAds) {
        if (edit) {
          let temp: AudienceSet[] = [];
          audienceSets.forEach((audienceSet: AudienceSet) => {
            if (audienceSet._id === data._id) {
              audienceSet = data;
            }

            temp = [...temp, audienceSet];
          });

          setAudienceSets(temp);
          setFilteredAudienceSets(temp);
        } else {
          setAudienceSets((prev) => [data, ...prev]);
          setFilteredAudienceSets((prev) => [data, ...prev]);
        }

        onSelectAudienceSet(data);
      }

      dispatch(
        toggleAlert({
          toggle: true,
          message: 'Your audience set has been saved successfully',
        }),
      );

      onClose();
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleOnRemoveAudience = (args: {
    target: AudienceSetTargetsData;
    targetType: string;
    flexibleIndex?: number;
  }) => {
    const { target, targetType, flexibleIndex } = args;
    let temp: AudienceSet = { ...formValues };
    if (targetType === 'flexible_spec') {
      let tempFlexibleSpec: AudienceSetTargetType[] = [
        ...(temp?.targets?.flexible_spec || []),
      ];

      tempFlexibleSpec[flexibleIndex] = {
        ...(tempFlexibleSpec?.[flexibleIndex] || {}),
        [target.type]: tempFlexibleSpec?.[flexibleIndex]?.[target.type]?.filter(
          (item: AudienceSetTargetsData) => item.id !== target.id,
        ),
      };

      if (isEmptyTargets(tempFlexibleSpec[flexibleIndex])) {
        tempFlexibleSpec.splice(flexibleIndex, 1);
      }

      temp = {
        ...temp,
        targets: { ...temp.targets, flexible_spec: tempFlexibleSpec },
      };
    } else if (targetType === 'exclusions') {
      temp = {
        ...temp,
        targets: {
          ...temp.targets,
          exclusions: {
            ...temp.targets.exclusions,
            [target.type]: temp.targets.exclusions[target.type].filter(
              (item: AudienceSetTargetsData) => item.id !== target.id,
            ),
          },
        },
      };
    } else {
      temp = {
        ...temp,
        targets: {
          ...temp.targets,
          [targetType]: temp.targets[targetType].filter(
            (item: AudienceSetTargetsData) => item.id !== target.id,
          ),
        },
      };
    }

    setFormValues(temp);
  };

  const handleOnChangeAudienceType = (e: ChangeEventType) => {
    setFormValues({
      ...formValues,
      audienceType: e.target.value,
      minReach: 0,
      maxReach: 0,
      targets: {
        custom_audiences: [],
        excluded_custom_audiences: [],
        flexible_spec: [],
        exclusions: { ...DEFAULT_TARGET_VALUES },
        targeting_relaxation_types: {
          lookalike: 1,
          custom_audience: 0,
        },
        targeting_automation: {
          advantage_audience: 0,
        },
        targeting_optimization: 'none',
      },
    });
  };

  const handleOnChange = (e: ChangeEventType) => {
    clearErrors(e.target.name);
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleCloseConfirmEdit = () => {
    setConfirmEdit((prev) => !prev);
  };

  const renderIncludedTargets = () => {
    if (
      formValues?.targets?.custom_audiences?.length === 0 &&
      formValues?.targets?.flexible_spec?.length === 0
    ) {
      return (
        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            No Included Targets
          </Typography>
        </Grid>
      );
    }

    if (formValues?.audienceType === 'detailed') {
      return formValues?.targets?.flexible_spec?.map(
        (type: AudienceSetTargetType, index: number) => {
          if (isEmptyTargets(type)) return <></>;

          return (
            <>
              {index > 0 ? (
                <Grid item xs={12}>
                  <Divider>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      and must also match
                    </Typography>
                  </Divider>
                </Grid>
              ) : null}

              {Object?.keys(type || {})?.map((key: string) => {
                if (type?.[key]?.length === 0 || !isArray(type?.[key])) {
                  return <></>;
                }

                return (
                  <>
                    <Grid
                      item
                      xs={12}
                      sx={{ justifyContent: 'space-between', display: 'flex' }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {constantStringToHumanized(key)}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {`Segment ${index + 1}`}
                      </Typography>
                    </Grid>

                    {type?.[key]?.map((target: AudienceSetTargetsData) => {
                      return (
                        <Grid item xs={12}>
                          <AudienceList
                            target={target}
                            formValues={formValues}
                            onRemoveAudience={handleOnRemoveAudience}
                            flexibleIndex={index}
                            targetType="flexible_spec"
                            withAction
                          />
                        </Grid>
                      );
                    })}
                  </>
                );
              })}
            </>
          );
        },
      );
    }

    return formValues?.targets?.custom_audiences?.map(
      (customAudience: AudienceSetTargetsData) => {
        return (
          <Grid item xs={12}>
            <AudienceList
              target={customAudience}
              formValues={formValues}
              onRemoveAudience={handleOnRemoveAudience}
              targetType="custom_audiences"
              withAction
            />
          </Grid>
        );
      },
    );
  };

  const renderExcludedTargets = () => {
    if (
      isEmptyTargets(formValues?.targets?.exclusions) &&
      formValues?.targets?.excluded_custom_audiences?.length === 0
    ) {
      return (
        <Grid item xs={12}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            No Excluded Targets
          </Typography>
        </Grid>
      );
    }

    if (formValues?.audienceType === 'detailed') {
      return Object?.keys(formValues?.targets?.exclusions)?.map(
        (key: string) => {
          if (formValues?.targets?.exclusions[key]?.length === 0) return <></>;

          return (
            <>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {constantStringToHumanized(key)}
                </Typography>
              </Grid>

              {formValues?.targets?.exclusions?.[key]?.map(
                (target: AudienceSetTargetsData) => {
                  return (
                    <Grid item xs={12}>
                      <AudienceList
                        target={target}
                        formValues={formValues}
                        onRemoveAudience={handleOnRemoveAudience}
                        targetType="exclusions"
                        withAction
                      />
                    </Grid>
                  );
                },
              )}
            </>
          );
        },
      );
    }

    return formValues?.targets?.excluded_custom_audiences?.map(
      (customAudience: AudienceSetTargetsData) => {
        return (
          <Grid item xs={12}>
            <AudienceList
              target={customAudience}
              formValues={formValues}
              onRemoveAudience={handleOnRemoveAudience}
              targetType="excluded_custom_audiences"
              withAction
            />
          </Grid>
        );
      },
    );
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {isCreateAds ? (
          <Grid item xs={12}>
            <PageHeader
              title={`${edit ? 'EDIT' : 'CREATE'} AUDIENCE SET`}
              textAlign="left"
              marginBottom="0"
            />
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            ...(!isMobile ? assetsSectionStyling(isCreateAds) : {}),
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('name', {
                  required: true,
                })}
                fullWidth
                required
                variant="standard"
                type="text"
                id="name"
                name="name"
                label="Audience Set Name"
                onChange={handleOnChange}
                value={formValues?.name}
                InputLabelProps={{ shrink: true }}
                size="small"
                autoFocus
                error={!!errors?.name}
                helperText={errors?.name && 'Audience set name is required'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="standard"
                type="text"
                id="description"
                name="description"
                label="Description"
                onChange={handleOnChange}
                value={formValues?.description}
                InputLabelProps={{ shrink: true }}
                size="small"
                multiline
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={formValues?.primary} />
                }
                label="Set as default audience set"
                value={formValues?.primary}
                onChange={(e: any) => {
                  setFormValues({
                    ...formValues,
                    primary: e.target.checked,
                  });
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  justifyContent: 'flex-start',
                  color: '#096F4D',
                  display: 'flex',
                  margin: 0,
                  alignItems: 'center',
                }}
                mb={1}
                component="div"
              >
                <Box component="span" sx={{ marginRight: '5px' }}>
                  Select your audience type
                </Box>

                <HtmlTooltip
                  disableInteractive
                  title={
                    <Fragment>
                      <Box component="div" sx={{ fontWeight: 'bold' }}>
                        Lookalike Audience
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        Lookalike audiences are lists of people to target with
                        advertising who are similar to (or 'look like') the
                        people currently engaging with your business.
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        <a
                          href="https://adsmanager.facebook.com/help/164749007013531/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Learn More
                        </a>
                      </Box>

                      <Box component="div" sx={{ fontWeight: 'bold' }}>
                        Detailed Audience
                      </Box>

                      <Box component="div" sx={{ marginBottom: '10px' }}>
                        Define your audience by including demographics interests
                        and behaviors.
                      </Box>

                      <div>
                        <a
                          href="https://www.facebook.com/business/help/182371508761821"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Learn More
                        </a>
                      </div>
                    </Fragment>
                  }
                >
                  <InfoTwoToneIcon
                    sx={{ fontSize: '18px', color: '#096F4D' }}
                  />
                </HtmlTooltip>
              </Typography>

              <RadioGroup
                row
                aria-labelledby="audienceType"
                name="audienceType"
                value={formValues?.audienceType}
                onChange={handleOnChangeAudienceType}
              >
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label="Lookalike Audience"
                />

                <FormControlLabel
                  value="detailed"
                  control={<Radio size="small" />}
                  label="Detailed Audience"
                />
              </RadioGroup>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  {formValues?.audienceType === 'custom' ? (
                    <CustomAudienceSetForm
                      formValues={formValues}
                      setFormValues={setFormValues}
                      brand={brand}
                      diySettings={diySettings}
                    />
                  ) : (
                    <DetailedAudienceSetForm
                      formValues={formValues}
                      setFormValues={setFormValues}
                      brand={brand}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            ...(!isMobile ? assetsSectionStyling(isCreateAds) : {}),
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{ flexDirection: 'column', display: 'flex' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Estimated Audience Size:{' '}
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ marginLeft: '10px' }}
                >
                  {reachLoading ? (
                    <Spinner />
                  ) : (
                    `${numberShortener(
                      reachEstimate.users_lower_bound,
                    )} - ${numberShortener(reachEstimate.users_upper_bound)}`
                  )}
                </Typography>
              </Typography>

              <Typography
                variant="caption"
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                <HtmlTooltip
                  title={`Geo Locations\n${geolocationToText(
                    defaultLocationSet,
                  )}\nDemographics\n${demographicsToText(
                    defaultDemographicsSet,
                    brand,
                  )}`}
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  <InfoTwoToneIcon
                    sx={{
                      fontSize: '18px',
                      color: '#096F4D',
                    }}
                  />
                </HtmlTooltip>

                <Box sx={{ marginLeft: '5px' }}>
                  Computed based on default geo locations and demographics sets
                </Box>
              </Typography>

              {reachError ? (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 'bold', color: 'red' }}
                >
                  {reachError}
                </Typography>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  marginRight: '10px',
                  textAlign: 'center',
                }}
              >
                Included
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    {renderIncludedTargets()}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/** Detailed targeting exclusions are not anymore supported by Facebook
             * Changes applied on March 31, 2025
             * https://www.facebook.com/business/help/458835214668072
             */}
            {formValues?.audienceType === 'custom' ? (
              <>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      marginRight: '10px',
                      textAlign: 'center',
                    }}
                  >
                    Excluded
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        {renderExcludedTargets()}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      <FloatingButton
        title="Save"
        onClick={handleSubmit(handleOnSubmit)}
        loading={loading}
        bottom="60px"
        right="180px"
        fixed
      />

      <FloatingButton
        title="Cancel"
        onClick={onClose}
        bottom="60px"
        right="85px"
        fixed
        theme="red"
      />

      <PopupModal open={confirmEdit} handleClose={handleCloseConfirmEdit}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ModalHeader title="Confirm Edit Template" />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', fontWeight: 'bold' }}
            >
              Modifying this template will affect other ad sets that use it.
              Confirm edit?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}
            >
              {formValues?.adsUsage
                ?.map((adset: CampaignAdSet) => `${adset.name} (${adset.id})`)
                ?.join('\n')}
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          sx={{ position: 'sticky', bottom: 0 }}
          mt={2}
        >
          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <PrimaryButton
              title="Cancel"
              type="button"
              handleOnClick={handleCloseConfirmEdit}
              loading={loading}
              marginRight5
            />

            <PrimaryButton
              title="Confirm"
              type="button"
              handleOnClick={handleOnSubmit}
              loading={loading}
            />
          </Grid>
        </Grid>
      </PopupModal>
    </Box>
  );
};

export default AudienceSetForm;
